import { atom } from "jotai";
import {
  tasksAtom,
  allTasksAtom,
  activeSlateAtom,
  showCompletedAtom,
  completedFilterAtom,
  slatesAtom,
  selectedTaskAtom,
} from "./index.js";
import { currentProjectIdAtom } from "./taskAtoms.js";

export const filteredTasksAtom = atom((get) => {
  const tasks = get(tasksAtom);
  const activeSlate = get(activeSlateAtom);
  const slates = get(slatesAtom);
  const currentProjectId = get(currentProjectIdAtom);

  if (!tasks.length) return [];

  let filtered = tasks.filter((task) => task.slate === slates[activeSlate]);

  if (currentProjectId === null) {
    filtered = filtered.filter((task) => !task.parent_id);
  } else {
    filtered = filtered.filter((task) => task.parent_id === currentProjectId);
  }

  // Note: Completion status filtering is handled by the backend
  // based on the filter parameter sent in the API request

  return filtered;
});

// Derived atom for current slate name
export const currentSlateNameAtom = atom((get) => {
  const slates = get(slatesAtom);
  const activeSlate = get(activeSlateAtom);
  return slates[activeSlate];
});

// Derived atom for task count by category with performance optimization
export const taskCountByCategoryAtom = atom((get) => {
  const tasks = get(filteredTasksAtom);
  return tasks.reduce((acc, task) => {
    acc[task.category] = (acc[task.category] || 0) + 1;
    return acc;
  }, {});
});

// Derived atom for urgent tasks count
export const urgentTasksCountAtom = atom((get) => {
  const tasks = get(filteredTasksAtom);
  return tasks.filter((task) => task.urgent).length;
});

// Derived atom for tasks by priority (high impact, low effort first)
export const prioritizedTasksAtom = atom((get) => {
  const tasks = get(filteredTasksAtom);
  return [...tasks].sort((a, b) => {
    // Sort by priority score (impact/effort ratio), then by urgency
    const priorityA = (a.impact || 1) / (a.effort || 1);
    const priorityB = (b.impact || 1) / (b.effort || 1);

    if (a.urgent !== b.urgent) {
      return b.urgent - a.urgent; // Urgent tasks first
    }

    return priorityB - priorityA;
  });
});

// Derived atom for task completion statistics
export const taskStatsAtom = atom((get) => {
  const tasks = get(tasksAtom);
  const activeSlate = get(activeSlateAtom);
  const slates = get(slatesAtom);

  const slateTasks = tasks.filter((task) => task.slate === slates[activeSlate]);
  const completed = slateTasks.filter(
    (task) => task.status === "complete"
  ).length;
  const pending = slateTasks.filter(
    (task) => task.status !== "complete"
  ).length;
  const total = slateTasks.length;

  return {
    total,
    completed,
    pending,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
});

// Derived atom for checking if a task is selected
export const isTaskSelectedAtom = atom((get) => (taskId) => {
  const selectedTask = get(selectedTaskAtom);
  return selectedTask?.task_id === taskId;
});

// Derived atom for tasks grouped by category
export const tasksGroupedByCategoryAtom = atom((get) => {
  const tasks = get(filteredTasksAtom);
  return tasks.reduce((acc, task) => {
    const category = task.category || "Uncategorized";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(task);
    return acc;
  }, {});
});

// Derived atom for current project info
export const currentProjectAtom = atom((get) => {
  const currentProjectId = get(currentProjectIdAtom);
  const allTasks = get(allTasksAtom);

  if (!currentProjectId) return null;

  return allTasks.find((task) => task.task_id === currentProjectId);
});

// Derived atom for full hierarchy path
export const hierarchyPathAtom = atom((get) => {
  const currentProject = get(currentProjectAtom);
  const allTasks = get(allTasksAtom);

  if (!currentProject) return [];

  const path = [];
  let currentTask = currentProject;

  // Build the path from current task up to the root
  while (currentTask) {
    path.unshift(currentTask);

    if (currentTask.parent_id) {
      currentTask = allTasks.find(
        (task) => task.task_id === currentTask.parent_id
      );
    } else {
      currentTask = null;
    }
  }

  return path;
});

// Derived atom for project breadcrumb display (supporting 3 levels)
export const breadcrumbDisplayAtom = atom((get) => {
  const hierarchyPath = get(hierarchyPathAtom);
  const slates = get(slatesAtom);
  const activeSlate = get(activeSlateAtom);

  const breadcrumb = [
    {
      name: slates[activeSlate],
      id: null,
      level: 0,
    },
  ];

  hierarchyPath.forEach((task, index) => {
    breadcrumb.push({
      name: task.task_name,
      id: task.task_id,
      level: index + 1,
      parentId: task.parent_id,
    });
  });

  return breadcrumb;
});

// Derived atom for enhanced tasks with calculated project properties
export const enhancedTasksAtom = atom((get) => {
  const tasks = get(filteredTasksAtom);
  const allTasks = get(allTasksAtom);

  return tasks.map((task) => {
    if (!task.hasChildren) return task;

    const children = allTasks.filter(
      (child) => child.parent_id === task.task_id && child.status !== "complete"
    );

    if (children.length === 0) return task;

    const derivedEffort = children.reduce(
      (sum, child) => sum + (child.effort || 0),
      0
    );
    const derivedImpact = Math.round(
      children.reduce((sum, child) => sum + (child.impact || 0), 0) /
        children.length
    );
    const derivedDueDate = children.reduce((latest, child) => {
      if (!child.due_date) return latest;
      if (!latest) return child.due_date;
      return new Date(child.due_date) > new Date(latest)
        ? child.due_date
        : latest;
    }, null);

    return {
      ...task,
      derivedEffort,
      derivedImpact,
      derivedDueDate,
      originalEffort: task.effort,
      originalImpact: task.impact,
      originalDueDate: task.due_date,
      effort: derivedEffort,
      impact: derivedImpact,
      due_date: derivedDueDate,
    };
  });
});
